/* eslint-disable prettier/prettier */
import { useEffect, useRef, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Label } from '../ui/Label';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { useAuth } from '../../context/AuthContext';
import { UserService } from '../../services/user.service';
import { User } from 'lucide-react';
import { CustomSelect } from '../ui/Select';
import { getCountries, getCountryCallingCode, parsePhoneNumberFromString } from 'libphonenumber-js';

const countryOptions = getCountries().map((country) => {
  const dialCode = `+${getCountryCallingCode(country)}`;
  return {
    label: `${dialCode} (${country})`,
    value: dialCode,
  };
});

export const EditProfileModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const { user, setUser } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [username, setUsername] = useState('');
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [profilePic, setProfilePic] = useState<File | null>(null);
  const [errors, setErrors] = useState<any>({});
  const [originalState, setOriginalState] = useState({ username: '', phone: '', countryCode: '' });

  const isEdited =
    username !== originalState.username ||
    phone !== originalState.phone ||
    countryCode !== originalState.countryCode ||
    !!profilePic;

  const handleUpdate = async () => {
    const newErrors: any = {};
    if (!username.trim()) newErrors.username = 'Username is required';
    if (!phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else {
      const fullNumber = `${countryCode}${phone}`;
      const parsed = parsePhoneNumberFromString(fullNumber);
      if (!parsed || !parsed.isValid()) {
        newErrors.phone = 'Invalid phone number';
      }
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    try {
      const formData = new FormData();
      formData.append('username', username);
      formData.append('phoneNumber', phone);
      formData.append('countryCode', countryCode);
      if (profilePic) formData.append('profilePicture', profilePic);

      const res = await UserService.updateProfile(formData);
      setUser(res.data);
      onClose();
    } catch (err: any) {
      const message = err?.response?.data?.message.message || 'Failed to update profile';
      setErrors((prev: any) => ({ ...prev, general: message }));
    }
  };

  useEffect(() => {
    if (isOpen && user) {
      setUsername(user.username);
      setPhone(user.phoneNumber);
      setCountryCode(user.countryCode || '+33');
      setOriginalState({
        username: user.username,
        phone: user.phoneNumber,
        countryCode: user.countryCode || '+33',
      });
      setProfilePic(null);
      setErrors({});
    }
  }, [isOpen, user]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && !['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      setErrors((prev: any) => ({
        ...prev,
        profilePic: 'Only PNG, JPEG, or JPG files are allowed',
      }));
      return;
    }
    setProfilePic(file || null);
    setErrors((prev: any) => ({ ...prev, profilePic: '' }));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Profile">
      <div className="flex flex-col items-center mb-6">
        <div className="relative cursor-pointer" onClick={() => fileInputRef.current?.click()}>
          {user?.profilePic ? (
            <img
              src={`${process.env.REACT_APP_API_BASE_URL}${user.profilePic}`}
              alt="profile picture"
              className="object-cover w-20 h-20 rounded-full"
            />
          ) : (
            <div className="flex items-center justify-center w-20 h-20 rounded-full bg-muted text-muted-foreground">
              <User className="w-6 h-6 text-primary" />
            </div>
          )}
          <input
            type="file"
            ref={fileInputRef}
            accept="image/png, image/jpeg, image/jpg"
            onChange={handleFileChange}
            className="hidden"
          />
        </div>
        {errors.profilePic && <p className="mt-2 text-sm text-red-500">{errors.profilePic}</p>}
      </div>

      <Label className="mt-4 mb-1">Username</Label>
      <Input
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        placeholder="Username"
        error={!!errors.username}
      />
      {errors.username && <p className="text-sm text-red-500">{errors.username}</p>}

      <Label className="mt-4 mb-1">Phone Number</Label>
      <div className="grid grid-cols-3 gap-2">
        <CustomSelect
          data={countryOptions}
          value={countryCode}
          onChange={(val) => setCountryCode(val)}
          error={!!errors.countryCode}
          errorMessage={errors.countryCode}
        />
        <div className="col-span-2">
          <Input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Phone number"
            error={!!errors.phone}
          />
        </div>
      </div>
      {errors.phone && <p className="text-sm text-red-500">{errors.phone}</p>}

      {errors.general && <p className="mt-4 text-sm text-center text-red-500">{errors.general}</p>}

      <div className="flex justify-end gap-3 mt-8">
        <Button variant="secondary" className="px-7 sm:px-10" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleUpdate} className="px-7 sm:px-10" disabled={!isEdited}>
          Save
        </Button>
      </div>
    </Modal>
  );
};
